/**
 * Growth Engine gate (frontend mirror of utils/growth_entitlement.py on the server).
 * The server decides (tenant.industry === 'euphoric_internal' or tenant.features.growth_engine === true) and
 * publishes it in the branding payload as features.growth_engine. The frontend only hides; the API denies.
 */
export const GROWTH_ENGINE_PATHS = new Set([
  '/admin/marketing/growth', '/admin/marketing/goal', '/admin/marketing/brain', '/admin/marketing/keywords', '/admin/marketing/keyword-gaps', '/admin/marketing/gaps', '/admin/marketing/authority', '/admin/marketing/competitors', '/admin/marketing/content', '/admin/marketing/geo',
  '/admin/marketing/technical-seo', '/admin/marketing/brand', '/admin/marketing/growth-settings',
  '/admin/marketing/content-production',
]);

export function isGrowthEnginePath(path) {
  return GROWTH_ENGINE_PATHS.has(String(path || '').split('?')[0]);
}

/**
 * Which Growth pages exist IN THIS BUILD.
 *
 * Entitlement and deployment are two different questions and were previously answered by one flag:
 * `features.growth_engine` said the tenant may use Growth, and the navigation then offered every
 * Growth page whether or not that page had shipped. Promoting one module at a time therefore meant
 * shipping a menu of links to pages that do not exist.
 *
 * This set is the deployment answer. It is the ONE place a build states which Growth pages it
 * carries, and it is deliberately a plain list so a promotion diff shows exactly what became
 * reachable. Entitlement still gates on top: a page must be both deployed AND entitled to appear.
 */
export const DEPLOYED_GROWTH_PATHS = new Set([
  // Production carries Growth AA, AB and AC. AD–AL remain on staging; listing a page here
  // without shipping its route would put a dead link in the navigation, which is the bug this set
  // exists to prevent. Extend it in the same commit that promotes the page.
  '/admin/marketing/technical-seo',
  '/admin/marketing/keywords',
  '/admin/marketing/competitors',
  '/admin/marketing/gaps',
  '/admin/marketing/authority',
  '/admin/marketing/content',
  '/admin/marketing/geo',
  '/admin/marketing/brand',
  // Growth AI — Content Production (execution). Ships with its route in the same commit.
  '/admin/marketing/content-production',
  // CC-1 — the Command Center and its two supporting surfaces.
  '/admin/marketing/growth',
  '/admin/marketing/goal',
  '/admin/marketing/brain',
]);

export function isGrowthPageDeployed(path) {
  return DEPLOYED_GROWTH_PATHS.has(String(path || '').split('?')[0]);
}

/**
 * The Growth area-nav items a given viewer should see: deployed in this build AND permitted by
 * entitlement. Exported so the navigation and its test exercise the same function.
 */
export function visibleGrowthNavItems(items, enabled) {
  return (items || []).filter((it) => isGrowthPageDeployed(it.path) && (enabled || !it.engine));
}

/**
 * Remove Growth Engine entries (and nothing else) from a menu tree when the tenant is not entitled.
 * The OS menu nests children under `submenu`; `subItems`/`items` are accepted defensively so a future
 * menu shape cannot silently reopen the gate.
 */
const CHILD_KEYS = ['submenu', 'subItems', 'items'];

export function applyGrowthEngineGate(items, enabled) {
  if (enabled) return items;
  const strip = (list) => (list || [])
    .filter((it) => !isGrowthEnginePath(it.path))
    .map((it) => {
      const key = CHILD_KEYS.find((k) => Array.isArray(it[k]));
      return key ? { ...it, [key]: strip(it[key]) } : it;
    });
  return strip(items);
}

/**
 * The Growth area navigation, in the order the architecture reads: Command (the cockpit) → Intelligence
 * (AA–AH) → Execution (AI–AK) → Settings. Pure data, so the gate and its test can both use it without
 * pulling in a component tree. Filter it with `visibleGrowthNavItems`.
 */
export const GROWTH_NAV = [
  { group: 'Command', items: [{ label: 'Command Center', path: '/admin/marketing/growth', engine: true }, { label: 'Growth Goal', path: '/admin/marketing/goal', engine: true }, { label: 'Growth Brain', path: '/admin/marketing/brain', engine: true }] },
  { group: 'Intelligence', items: [{ label: 'Keywords', path: '/admin/marketing/keywords', engine: true }, { label: 'Keyword Gaps', path: '/admin/marketing/keyword-gaps', engine: true }, { label: 'Competitors', path: '/admin/marketing/competitors', engine: true }, { label: 'Gap Intelligence', path: '/admin/marketing/gaps', engine: true }, { label: 'Technical SEO', path: '/admin/marketing/technical-seo', engine: true }, { label: 'Authority', path: '/admin/marketing/authority', engine: true }, { label: 'Content', path: '/admin/marketing/content', engine: true }, { label: 'GEO / AI Visibility', path: '/admin/marketing/geo', engine: true }, { label: 'Brand', path: '/admin/marketing/brand', engine: true }] },
  { group: 'Execution', items: [{ label: 'Content Production', path: '/admin/marketing/content-production', engine: true }] },
  { group: 'Settings', items: [{ label: 'Growth Settings', path: '/admin/marketing/growth-settings', engine: true }] },
];
